import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./customizeSpotTheme.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { apiUrl } from "../../../utils/apiUrl";
import {
  countQuestionsInRounds,
  parseRoundsFromThemeData,
  validateQuizRoundsForSave,
  sanitizeQuizRoundsForSave,
  migrateQuizRoundsForAdmin,
} from "../../../utils/quizTheme";
import QuizRoundsEditor from "./QuizRoundsEditor";

const ITEM_W = 206;
const ITEM_H = 296;
const ITEM_NAME_MAX_LEN = 50;

function toSlugKey(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}
function formatKey(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Categories with an odd item count need one more item to complete a pair. */
function getFirstIncompletePairCategory(cats) {
  const idx = cats.findIndex((c) => c.items.length % 2 === 1);
  if (idx === -1) return null;
  return { catIndex: idx, displayName: cats[idx].displayName };
}

/** Renders an item image with a proper fallback.
 *  Resets error state whenever `src` changes (e.g. after a new upload),
 *  so stale inline-style display:none never survives a src update. */
function ItemImage({ src, alt, className }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);

  if (!src || failed) {
    return (
      <div className={`catmgr-item-no-img ${className || ""}`}>
        <i className="fa-solid fa-image" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={`catmgr-item-img ${className || ""}`}
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

const CustomizeSpotTheme = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((s) => s.theme);

  // categories: [{ key, displayName, items:[{image,name}] }]
  const [categories, setCategories]   = useState([]);
  const [useQuizRounds, setUseQuizRounds] = useState(false);
  const [quizRounds, setQuizRounds]     = useState([]);
  const [filterText, setFilterText]   = useState("");
  const [saving, setSaving]           = useState(false);

  // ── Category modals ─────────────────────────────────────────
  const [addCatOpen,   setAddCatOpen]   = useState(false);
  const [newCatName,   setNewCatName]   = useState("");
  const [editCatModal, setEditCatModal] = useState(null); // { index }
  const [editCatName,  setEditCatName]  = useState("");

  // ── Item modal (shared add/edit) ────────────────────────────
  // mode: 'add' | 'edit'
  const [itemModal,    setItemModal]    = useState(null); // { mode, catIndex, itemIndex? }
  const [itemName,     setItemName]     = useState("");
  const [itemFile,     setItemFile]     = useState(null);   // File object
  const [itemPreview,  setItemPreview]  = useState("");     // blob URL for preview
  const [itemFilename, setItemFilename] = useState("");     // existing filename (edit mode)
  const [itemUploading,setItemUploading]= useState(false);
  const itemFileRef = useRef(null);

  // ── Load data ────────────────────────────────────────────────
  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    const parsed = parseRoundsFromThemeData(data);
    if (parsed.length > 0) {
      setUseQuizRounds(true);
      setCategories([]);
      setQuizRounds(migrateQuizRoundsForAdmin(JSON.parse(JSON.stringify(parsed))));
      return;
    }
    setUseQuizRounds(false);
    setQuizRounds([]);
    // Avoid wiping local list when API returns a partial theme object without this key.
    if (!("category_images_names" in data)) return;
    const cin  = data.category_images_names || {};
    const cats = ("categories" in data) ? (data.categories || []) : [];
    const result = [];
    cats.forEach((displayName) => {
      const key = toSlugKey(displayName);
      result.push({ key, displayName, items: [...(cin[key] || [])] });
    });
    Object.keys(cin).forEach((key) => {
      if (!result.find((r) => r.key === key))
        result.push({ key, displayName: formatKey(key), items: [...(cin[key] || [])] });
    });
    setCategories(result);
  }, [data]);

  // ── Save all ─────────────────────────────────────────────────
  const handleSave = async (cats) => {
    if (!currentTheme) { Swal.fire("No theme", "Select a theme first.", "error"); return; }
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            categories: cats.map((c) => c.displayName),
            category_images_names: Object.fromEntries(cats.map((c) => [c.key, c.items])),
          },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", "Categories updated.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed.", "error");
    }
    setSaving(false);
  };

  const handleSaveRounds = async () => {
    if (!currentTheme) {
      Swal.fire("No theme", "Select a theme first.", "error");
      return;
    }
    const err = validateQuizRoundsForSave(quizRounds);
    if (err) {
      Swal.fire("Check your content", err, "warning");
      return;
    }
    const cleaned = sanitizeQuizRoundsForSave(quizRounds);
    const total = countQuestionsInRounds(cleaned);
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            rounds: cleaned,
            number_of_slides: total,
          },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", "Quiz rounds updated.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed.", "error");
    }
    setSaving(false);
  };

  // ── Category CRUD ────────────────────────────────────────────
  const handleAddCategory = async () => {
    const name = newCatName.trim();
    if (!name) { Swal.fire("Required", "Category name is required.", "warning"); return; }
    const key = toSlugKey(name);
    if (categories.find((c) => c.key === key)) {
      Swal.fire("Duplicate", "A category with this name already exists.", "warning"); return;
    }
    const updatedCats = [...categories, { key, displayName: name, items: [] }];
    setCategories(updatedCats);
    setAddCatOpen(false);
    setNewCatName("");
    await handleSave(updatedCats);
  };

  const handleEditCategory = async () => {
    const name = editCatName.trim();
    if (!name) { Swal.fire("Required", "Category name is required.", "warning"); return; }
    const updatedCats = categories.map((c, i) => i === editCatModal.index ? { ...c, displayName: name } : c);
    setCategories(updatedCats);
    setEditCatModal(null);
    setEditCatName("");
    await handleSave(updatedCats);
  };

  const handleDeleteCategory = (index) => {
    Swal.fire({
      title: "Delete Category?",
      text: `"${categories[index].displayName}" and all its items will be removed.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Yes, delete",
    }).then(async (r) => {
      if (r.isConfirmed) {
        const updatedCats = categories.filter((_, i) => i !== index);
        setCategories(updatedCats);
        await handleSave(updatedCats);
      }
    });
  };

  // ── Item image upload helper ──────────────────────────────────
  const uploadItemImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("themeName", String(currentTheme));
    const res  = await fetch(apiUrl("/uploadCategoryImage"), {
      method: "POST",
      headers: { Authorization: `Bearer ${adminToken}` },
      body: fd,
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Upload failed");
    return json.filename;
  };

  // ── Item file picker (with dimension validation) ──────────────
  const handleItemFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpeg", "jpg", "png"].includes(ext)) {
      Swal.fire("Invalid File", "Only JPEG / JPG / PNG allowed.", "error");
      e.target.value = ""; return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (img.width !== ITEM_W || img.height !== ITEM_H) {
        URL.revokeObjectURL(url);
        Swal.fire(
          "Wrong Dimensions",
          `Image must be exactly ${ITEM_W} × ${ITEM_H} px. Uploaded image is ${img.width} × ${img.height} px.`,
          "error"
        );
        if (itemFileRef.current) itemFileRef.current.value = "";
        return;
      }
      setItemFile(file);
      setItemPreview(url);
    };
    img.src = url;
  };

  const closeItemModal = () => {
    if (itemPreview) URL.revokeObjectURL(itemPreview);
    setItemModal(null);
    setItemName(""); setItemFile(null); setItemPreview(""); setItemFilename("");
    if (itemFileRef.current) itemFileRef.current.value = "";
  };

  /** Block adding to another category while a pair is incomplete elsewhere. */
  const assertCanAddItemToCategory = (catIndex) => {
    const pending = getFirstIncompletePairCategory(categories);
    if (!pending || pending.catIndex === catIndex) return true;
    Swal.fire({
      icon: "warning",
      title: "Complete the pair first",
      html:
        `Please add <strong>one more item</strong> in <strong>${pending.displayName}</strong> to make a pair before adding items to <strong>${categories[catIndex].displayName}</strong>.`,
    });
    return false;
  };

  const handleItemNameChange = (val) => {
    let next = val;
    if (next.length > ITEM_NAME_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Item name can be at most ${ITEM_NAME_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, ITEM_NAME_MAX_LEN);
    }
    setItemName(next);
  };

  const openAddItemModal = (catIndex) => {
    if (!assertCanAddItemToCategory(catIndex)) return;
    setItemModal({ mode: "add", catIndex });
    setItemName("");
    setItemFile(null);
    setItemPreview("");
    setItemFilename("");
  };

  // ── Item CRUD ─────────────────────────────────────────────────
  const handleSaveItem = async () => {
    const trimmed = itemName.trim();
    if (!trimmed) {
      Swal.fire("Required", "Item name is required.", "warning");
      return;
    }
    if (trimmed.length > ITEM_NAME_MAX_LEN) {
      Swal.fire(
        "Invalid name",
        `Item name must be at most ${ITEM_NAME_MAX_LEN} characters.`,
        "warning"
      );
      return;
    }
    const name = trimmed;

    const { mode, catIndex, itemIndex } = itemModal;

    if (mode === "add" && !assertCanAddItemToCategory(catIndex)) {
      return;
    }

    if (mode === "add" && !itemFile) {
      Swal.fire("Image required", "Please choose an image file for the new item (name and image are both required).", "warning");
      return;
    }

    let filename = itemFilename;
    if (itemFile) {
      setItemUploading(true);
      try { filename = await uploadItemImage(itemFile); }
      catch (e) { Swal.fire("Upload Error", e.message, "error"); setItemUploading(false); return; }
      setItemUploading(false);
    }

    if (mode === "add" && !filename) {
      Swal.fire("Image required", "Image upload failed or is missing. Please try again.", "warning");
      return;
    }
    if (mode === "edit" && !filename) {
      Swal.fire("Image required", "Each item must have an image.", "warning");
      return;
    }

    let updatedCats;
    if (mode === "add") {
      updatedCats = categories.map((c, i) =>
        i === catIndex ? { ...c, items: [...c.items, { image: filename, name }] } : c
      );
    } else {
      updatedCats = categories.map((c, ci) =>
        ci === catIndex
          ? { ...c, items: c.items.map((it, ii) => ii === itemIndex ? { image: filename, name } : it) }
          : c
      );
    }

    setCategories(updatedCats);
    closeItemModal();
    await handleSave(updatedCats);
  };

  const handleDeleteItem = (catIndex, itemIndex) => {
    Swal.fire({
      title: "Delete Item?",
      text: `"${categories[catIndex].items[itemIndex].name}" will be removed.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Yes, delete",
    }).then(async (r) => {
      if (r.isConfirmed) {
        const updatedCats = categories.map((c, ci) =>
          ci === catIndex ? { ...c, items: c.items.filter((_, ii) => ii !== itemIndex) } : c
        );
        setCategories(updatedCats);
        await handleSave(updatedCats);
      }
    });
  };

  // ── Filter ────────────────────────────────────────────────────
  const fl = filterText.toLowerCase();
  const filteredCategories = categories.filter((c) => {
    if (!fl) return true;
    if (c.displayName.toLowerCase().includes(fl)) return true;
    return c.items.some((it) => it.name.toLowerCase().includes(fl));
  });

  const S3        = process.env.REACT_APP_S3_PATH || "";
  const gameName  = data?.themeName || data?.themename || "Light Camera Pop!";
  const totalItems = categories.reduce((s, c) => s + c.items.length, 0);
  const quizQuestionTotal = useQuizRounds ? countQuestionsInRounds(quizRounds) : 0;

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className={`catmgr-page${useQuizRounds ? " catmgr-page--quiz" : ""}`}>
        {useQuizRounds ? (
          <>
            <header className="catmgr-header catmgr-header--quiz">
              <div className="catmgr-header-left">
                <h2 className="catmgr-title catmgr-title--quiz">
                  <span className="catmgr-title-icon" aria-hidden>
                    <i className="fa-solid fa-circle-question" />
                  </span>
                  {gameName}
                  <span className="catmgr-title-sep">—</span>
                  <span className="catmgr-title-muted">Quiz rounds</span>
                </h2>
                <p className="catmgr-subtitle catmgr-subtitle--quiz">
                  Configure each round below. Every round allows 1–10 questions (the last question in a round cannot
                  be removed). Use <strong>Save all rounds</strong> when you are done.
                </p>
              </div>
              <div className="catmgr-header-right catmgr-header-right--quiz">
                <span className="catmgr-stat-pill" title="Total questions across all rounds">
                  {quizQuestionTotal} questions
                </span>
              </div>
            </header>
            <QuizRoundsEditor
              rounds={quizRounds}
              setRounds={setQuizRounds}
              saving={saving}
              onSave={handleSaveRounds}
              adminToken={adminToken}
              currentTheme={currentTheme}
              s3Base={S3}
            />
          </>
        ) : (
          <>
        {/* ── Page Header ── */}
        <div className="catmgr-header">
          <div className="catmgr-header-left">
            <h2 className="catmgr-title">
              <i className="fa-solid fa-layer-group" /> &nbsp;{gameName} — Category Manager
            </h2>
            <p className="catmgr-subtitle">
                  <>
                    {categories.length} categories &nbsp;·&nbsp; {totalItems} total items
                    <br />
                    <span className="catmgr-pair-hint">
                      Add items in pairs per category (each slide needs two options). If one category has an
                      unpaired item, add the matching item there before adding to other categories.
                    </span>
                  </>
            </p>
          </div>
          <div className="catmgr-header-right">
            <input
              className="catmgr-search"
              type="text"
              placeholder="Filter categories or items…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
            <button
              className="catmgr-btn catmgr-btn-primary"
              onClick={() => handleSave(categories)}
              disabled={saving}
            >
              {saving ? "Saving…" : <><i className="fa-solid fa-floppy-disk" /> Save All</>}
            </button>
          </div>
        </div>

        {/* ── Two-column body ── */}
        <div className="catmgr-body">

          {/* ══ LEFT: Categories list ══════════════════════════════ */}
          <div className="catmgr-left-panel">
            <div className="catmgr-panel-card">
              <div className="catmgr-panel-head">
                <span className="catmgr-panel-title">
                  <i className="fa-solid fa-list" /> Categories
                </span>
                <button
                  className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                  onClick={() => setAddCatOpen(true)}
                >
                  <i className="fa-solid fa-plus" /> Add
                </button>
              </div>

              {categories.length === 0 && (
                <p className="catmgr-no-items">No categories yet.</p>
              )}

              <ul className="catmgr-cat-list">
                {categories.map((cat, i) => (
                  <li key={cat.key} className="catmgr-cat-row">
                    <div className="catmgr-cat-info">
                      <span className="catmgr-cat-display">{cat.displayName}</span>
                      <span className="catmgr-cat-key">{cat.key}</span>
                    </div>
                    <div className="catmgr-cat-actions">
                      <button
                        className="catmgr-icon-btn catmgr-icon-btn-sm"
                        title="Edit"
                        onClick={() => { setEditCatModal({ index: i }); setEditCatName(cat.displayName); }}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                        title="Delete"
                        onClick={() => handleDeleteCategory(i)}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ══ RIGHT: Category items ══════════════════════════════ */}
          <div className="catmgr-right-panel">
            {filteredCategories.length === 0 && (
              <div className="catmgr-empty">No categories found.</div>
            )}

            {filteredCategories.map((cat) => {
              const catIndex = categories.indexOf(cat);
              const visibleItems = fl
                ? cat.items.filter(
                    (it) =>
                      it.name.toLowerCase().includes(fl) ||
                      cat.displayName.toLowerCase().includes(fl)
                  )
                : cat.items;

              return (
                <div className="catmgr-card" key={cat.key}>
                  <div className="catmgr-card-header">
                    <div className="catmgr-card-title-block">
                      <h3 className="catmgr-card-name">{cat.displayName}</h3>
                      <span className="catmgr-card-key">{cat.key}</span>
                      <span className="catmgr-badge">{cat.items.length} items</span>
                    </div>
                    <button
                      className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                      onClick={() => openAddItemModal(catIndex)}
                    >
                      <i className="fa-solid fa-plus" /> Add Item
                    </button>
                  </div>

                  {visibleItems.length === 0 ? (
                    <p className="catmgr-no-items">No items yet — click Add Item.</p>
                  ) : (
                    <div className="catmgr-items-grid">
                      {visibleItems.map((item) => {
                        const itemIndex = cat.items.indexOf(item);
                        return (
                          <div className="catmgr-item-card" key={itemIndex}>
                            <div className="catmgr-item-img-wrap">
                              <ItemImage
                                src={item.image ? S3 + item.image : ""}
                                alt={item.name}
                              />
                            </div>
                            <div className="catmgr-item-info">
                              <span className="catmgr-item-name" title={item.name}>{item.name}</span>
                            </div>
                            <div className="catmgr-item-btns">
                              <button
                                className="catmgr-icon-btn catmgr-icon-btn-sm"
                                title="Edit"
                                onClick={() => {
                                  setItemModal({ mode: "edit", catIndex, itemIndex });
                                  setItemName(
                                    String(item.name ?? "").slice(0, ITEM_NAME_MAX_LEN)
                                  );
                                  setItemFile(null); setItemPreview(""); setItemFilename(item.image || "");
                                }}
                              >
                                <i className="fa-solid fa-pen" />
                              </button>
                              <button
                                className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                                title="Delete"
                                onClick={() => handleDeleteItem(catIndex, itemIndex)}
                              >
                                <i className="fa-solid fa-trash" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
          </>
        )}
      </div>

      {/* ══ Add Category Modal ══════════════════════════════════ */}
      {addCatOpen && (
        <>
          <div className="catmgr-backdrop" onClick={() => setAddCatOpen(false)} />
          <div className="catmgr-modal">
            <h3>Add Category</h3>
            <label className="catmgr-modal-label">Display name</label>
            <input
              autoFocus className="catmgr-modal-input" placeholder="e.g. Travel & Places"
              value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
            />
            {newCatName.trim() && (
              <p className="catmgr-modal-hint">Key: <code>{toSlugKey(newCatName)}</code></p>
            )}
            <div className="catmgr-modal-actions">
              <button onClick={() => setAddCatOpen(false)}>Cancel</button>
              <button className="primary" onClick={handleAddCategory}>Add</button>
            </div>
          </div>
        </>
      )}

      {/* ══ Edit Category Modal ══════════════════════════════════ */}
      {editCatModal && (
        <>
          <div className="catmgr-backdrop" onClick={() => setEditCatModal(null)} />
          <div className="catmgr-modal">
            <h3>Edit Category Name</h3>
            <label className="catmgr-modal-label">Display name</label>
            <input
              autoFocus className="catmgr-modal-input" value={editCatName}
              onChange={(e) => setEditCatName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleEditCategory()}
            />
            <div className="catmgr-modal-actions">
              <button onClick={() => setEditCatModal(null)}>Cancel</button>
              <button className="primary" onClick={handleEditCategory}>Save</button>
            </div>
          </div>
        </>
      )}

      {/* ══ Add / Edit Item Modal ════════════════════════════════ */}
      {itemModal && (
        <>
          <div className="catmgr-backdrop" onClick={closeItemModal} />
          <div className="catmgr-modal catmgr-modal-wide">
            <h3>
              {itemModal.mode === "add"
                ? `Add Item — ${categories[itemModal.catIndex]?.displayName}`
                : "Edit Item"}
            </h3>

            {/* Item name */}
            <label className="catmgr-modal-label">Item name</label>
            <input
              autoFocus
              className="catmgr-modal-input"
              placeholder="e.g. Pizza forever"
              value={itemName}
              onChange={(e) => handleItemNameChange(e.target.value)}
              maxLength={ITEM_NAME_MAX_LEN}
            />
            <p className="catmgr-modal-hint" style={{ marginTop: 4 }}>
              * Maximum {ITEM_NAME_MAX_LEN} characters.
            </p>

            {/* Image upload */}
            <label className="catmgr-modal-label" style={{ marginTop: 16 }}>
              Image &nbsp;
              <span className="catmgr-optional">
                (JPEG / JPG / PNG — exactly {ITEM_W} × {ITEM_H} px)
              </span>
            </label>

            {/* Preview */}
            <div className="catmgr-upload-preview">
              {itemPreview ? (
                <img src={itemPreview} alt="preview" className="catmgr-upload-thumb" />
              ) : itemFilename ? (
                <ItemImage src={S3 + itemFilename} alt="current" className="catmgr-upload-thumb" />
              ) : (
                <div className="catmgr-upload-placeholder">
                  <i className="fa-solid fa-image" />
                  <span>{ITEM_W} × {ITEM_H} px</span>
                </div>
              )}
            </div>

            <input
              ref={itemFileRef}
              type="file"
              accept=".jpeg,.jpg,.png"
              className="catmgr-file-input"
              onChange={handleItemFileChange}
            />
            <p className="catmgr-modal-hint">
              * Image required for new items. Exactly {ITEM_W} × {ITEM_H} px. Max 5 MB.
            </p>

            <div className="catmgr-modal-actions">
              <button onClick={closeItemModal} disabled={itemUploading}>Cancel</button>
              <button className="primary" onClick={handleSaveItem} disabled={itemUploading}>
                {itemUploading ? "Uploading…" : itemModal.mode === "add" ? "Add" : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CustomizeSpotTheme;
